import React, { useEffect, useState, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import {
  PhoneCall,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Filter,
  Clock,
  RefreshCcw,
  ArrowLeft,
  ArrowRight,
  Play,
  Pause,
  Download,
  User,
  Calendar,
  Search,
  ChevronDown,
  X,
  Phone,
} from "lucide-react";

import getClientConfig from "../config";

const { API_BASE_URL } = getClientConfig();

// Format duration in seconds to MM:SS or HH:MM:SS
function formatDuration(seconds) {
  if (!seconds || seconds === 0) return "0:00";
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

// Format date nicely
function formatDate(dateStr) {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleString("en-PK", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Disposition badge component
function DispositionBadge({ disposition }) {
  const styles = {
    ANSWERED: "bg-green-100 text-green-700",
    "NO ANSWER": "bg-yellow-100 text-yellow-700",
    BUSY: "bg-orange-100 text-orange-700",
    FAILED: "bg-red-100 text-red-600",
    CONGESTION: "bg-red-100 text-red-600",
  };

  return (
    <span
      className={`text-xs px-2 py-1 rounded-full font-semibold ${styles[disposition] || "bg-gray-100 text-gray-600"}`}
    >
      {disposition}
    </span>
  );
}

// Direction icon component
function DirectionIcon({ direction }) {
  if (direction === "inbound") {
    return <PhoneIncoming className="w-4 h-4 text-blue-500" title="Inbound" />;
  } else if (direction === "outbound") {
    return <PhoneOutgoing className="w-4 h-4 text-green-500" title="Outbound" />;
  }
  return <PhoneCall className="w-4 h-4 text-gray-500" title="Internal" />;
}

// Audio player component for recordings
function AudioPlayer({ recordingUrl, uniqueId }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);

  const token = localStorage.getItem("auth_token");
  
  // recordingUrl already includes /api/voice/recordings/... 
  // API_BASE_URL might be http://localhost:8080/api or http://localhost:8080
  // So we need to handle both cases
  const baseUrl = API_BASE_URL.replace(/\/api\/?$/, ''); // Remove trailing /api if present
  const fullUrl = `${baseUrl}${recordingUrl}`;

  const togglePlay = async () => {
    setError(null);
    
    if (!audioRef.current) {
      setIsLoading(true);
      try {
        console.log("Fetching recording from:", fullUrl);
        const response = await fetch(fullUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }

        const blob = await response.blob();
        console.log("Recording blob size:", blob.size, "type:", blob.type);
        
        if (blob.size === 0) {
          throw new Error("Recording file is empty");
        }

        const url = URL.createObjectURL(blob);
        
        audioRef.current = new Audio(url);
        audioRef.current.onended = () => setIsPlaying(false);
        audioRef.current.onerror = (e) => {
          console.error("Audio error:", e);
          setError("Failed to play audio");
          setIsPlaying(false);
        };
        audioRef.current.oncanplaythrough = () => {
          console.log("Audio can play through");
        };
        
        // Wait for audio to be ready before playing
        await new Promise((resolve, reject) => {
          audioRef.current.oncanplay = resolve;
          audioRef.current.onerror = reject;
          audioRef.current.load();
        });

        await audioRef.current.play();
        setIsPlaying(true);
      } catch (err) {
        console.error("Error playing recording:", err);
        setError(err.message || "Failed to load recording");
        toast.error(err.message || "Failed to load recording");
      } finally {
        setIsLoading(false);
      }
    } else {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        try {
          await audioRef.current.play();
          setIsPlaying(true);
        } catch (err) {
          console.error("Error resuming playback:", err);
          setError("Failed to resume playback");
        }
      }
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(fullUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to download");

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `recording-${uniqueId}.wav`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Failed to download recording");
    }
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
      }
    };
  }, []);

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={togglePlay}
        disabled={isLoading}
        className={`p-1.5 rounded-full transition disabled:opacity-50 ${
          error 
            ? "bg-red-100 text-red-600" 
            : "bg-blue-100 text-blue-600 hover:bg-blue-200"
        }`}
        title={error || (isPlaying ? "Pause" : "Play")}
      >
        {isLoading ? (
          <RefreshCcw className="w-4 h-4 animate-spin" />
        ) : error ? (
          <X className="w-4 h-4" />
        ) : isPlaying ? (
          <Pause className="w-4 h-4" />
        ) : (
          <Play className="w-4 h-4" />
        )}
      </button>
      <button
        onClick={handleDownload}
        className="p-1.5 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
        title="Download"
      >
        <Download className="w-4 h-4" />
      </button>
    </div>
  );
}

// Searchable Client Selector Component
function ClientSelector({ clients, selectedClient, onSelect, loading }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredClients = clients.filter((client) => {
    const searchLower = search.toLowerCase();
    return (
      client.extension?.toLowerCase().includes(searchLower) ||
      client.name?.toLowerCase().includes(searchLower) ||
      client.cnic?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 border border-gray-300 rounded-lg px-4 py-2.5 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
      >
        {loading ? (
          <span className="flex items-center gap-2 text-gray-500">
            <RefreshCcw className="w-4 h-4 animate-spin" />
            Loading clients...
          </span>
        ) : selectedClient ? (
          <span className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-blue-600" />
            <span className="font-mono font-medium">{selectedClient.extension}</span>
            <span className="text-gray-500">-</span>
            <span className="text-gray-700">{selectedClient.name}</span>
          </span>
        ) : (
          <span className="text-gray-500">Select a client/extension...</span>
        )}
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by extension, name, or CNIC..."
                className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                >
                  <X className="w-3 h-3 text-gray-400" />
                </button>
              )}
            </div>
          </div>

          {/* Client List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredClients.length === 0 ? (
              <div className="px-4 py-6 text-center text-gray-500">
                {clients.length === 0
                  ? "No clients found"
                  : "No matching clients"}
              </div>
            ) : (
              filteredClients.map((client) => (
                <button
                  key={client.id}
                  onClick={() => {
                    onSelect(client);
                    setIsOpen(false);
                    setSearch("");
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition text-left ${
                    selectedClient?.id === client.id ? "bg-blue-50" : ""
                  }`}
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Phone className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-blue-600">
                        {client.extension}
                      </span>
                      {client.li_enabled && (
                        <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">
                          REC
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 truncate">
                      {client.name}
                    </div>
                    {client.cnic && (
                      <div className="text-xs text-gray-400">{client.cnic}</div>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function VoiceCDRTable({ voiceClientId: propClientId }) {
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);

  const [cdrs, setCDRs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    dst: "",
    disposition: "",
    direction: "",
    start_date: "",
    end_date: "",
  });
  const [grouped, setGrouped] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const totalPages = Math.ceil(total / itemsPerPage);
  const token = localStorage.getItem("auth_token");

  // Fetch all voice clients
  useEffect(() => {
    setLoadingClients(true);
    fetch(`${API_BASE_URL}/voice/clients?limit=1000`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch clients");
        return res.json();
      })
      .then((data) => {
        const clientList = data.data || data || [];
        setClients(clientList);

        // If propClientId is provided, find and select that client
        if (propClientId) {
          const client = clientList.find(
            (c) => c.id === parseInt(propClientId)
          );
          if (client) setSelectedClient(client);
        }

        setLoadingClients(false);
      })
      .catch((err) => {
        console.error("Failed to fetch clients:", err);
        toast.error("Failed to load voice clients");
        setLoadingClients(false);
      });
  }, [token, propClientId]);

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams({
      limit: itemsPerPage.toString(),
      offset: ((currentPage - 1) * itemsPerPage).toString(),
      grouped: grouped.toString(),
    });

    if (filters.dst) params.append("dst", filters.dst);
    if (filters.disposition) params.append("disposition", filters.disposition);
    if (filters.direction) params.append("direction", filters.direction);
    if (filters.start_date) params.append("start_date", filters.start_date);
    if (filters.end_date) params.append("end_date", filters.end_date);

    return params.toString();
  }, [filters, currentPage, itemsPerPage, grouped]);

  const fetchCDRs = useCallback(() => {
    if (!selectedClient?.id) {
      setCDRs([]);
      setTotal(0);
      return;
    }

    setLoading(true);

    fetch(
      `${API_BASE_URL}/voice/clients/${selectedClient.id}/cdr?${buildQuery()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    )
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch CDRs");
        return res.json();
      })
      .then((data) => {
        setCDRs(data.data || []);
        setTotal(data.total ?? data.count ?? 0);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch CDRs:", err);
        toast.error("Failed to load CDRs");
        setLoading(false);
      });
  }, [selectedClient, buildQuery, token]);

  useEffect(() => {
    fetchCDRs();
  }, [fetchCDRs]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      dst: "",
      disposition: "",
      direction: "",
      start_date: "",
      end_date: "",
    });
    setCurrentPage(1);
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setCurrentPage(1);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <PhoneCall className="text-blue-600" />
            Call Detail Records
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            View call history and recordings for voice clients
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={grouped}
              onChange={(e) => setGrouped(e.target.checked)}
              className="rounded border-gray-300"
            />
            Group by call
          </label>
          <button
            onClick={fetchCDRs}
            disabled={loading || !selectedClient}
            className="flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-2 rounded-lg text-sm hover:bg-blue-200 transition disabled:opacity-50"
          >
            <RefreshCcw size={16} className={loading ? "animate-spin" : ""} />{" "}
            Refresh
          </button>
        </div>
      </div>

      {/* Client Selector */}
      <div className="bg-white border rounded-xl p-5 mb-6 shadow">
        <div className="flex items-center gap-2 text-gray-600 font-medium mb-3">
          <User size={18} /> Select Voice Client
        </div>
        <ClientSelector
          clients={clients}
          selectedClient={selectedClient}
          onSelect={handleClientSelect}
          loading={loadingClients}
        />
      </div>

      {/* Filters - Only show when client is selected */}
      {selectedClient && (
        <div className="bg-white border rounded-xl p-5 mb-6 shadow space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-gray-600 font-medium">
              <Filter size={18} /> Filters
            </div>
            <button
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Clear all
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div>
              <label className="text-xs text-gray-500 flex gap-1 items-center mb-1">
                <PhoneIncoming className="w-4 h-4" /> Destination
              </label>
              <input
                type="text"
                name="dst"
                value={filters.dst}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g. 03001234567"
              />
            </div>

            <div>
              <label className="text-xs text-gray-500 flex gap-1 items-center mb-1">
                <Clock className="w-4 h-4" /> Disposition
              </label>
              <select
                name="disposition"
                value={filters.disposition}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All</option>
                <option value="ANSWERED">Answered</option>
                <option value="NO ANSWER">No Answer</option>
                <option value="BUSY">Busy</option>
                <option value="FAILED">Failed</option>
                <option value="CONGESTION">Congestion</option>
              </select>
            </div>

            <div>
              <label className="text-xs text-gray-500 flex gap-1 items-center mb-1">
                <PhoneCall className="w-4 h-4" /> Direction
              </label>
              <select
                name="direction"
                value={filters.direction}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All</option>
                <option value="inbound">Inbound</option>
                <option value="outbound">Outbound</option>
                <option value="internal">Internal</option>
              </select>
            </div>

            <div>
              <label className="text-xs text-gray-500 flex gap-1 items-center mb-1">
                <Calendar className="w-4 h-4" /> Start Date
              </label>
              <input
                type="date"
                name="start_date"
                value={filters.start_date}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="text-xs text-gray-500 flex gap-1 items-center mb-1">
                <Calendar className="w-4 h-4" /> End Date
              </label>
              <input
                type="date"
                name="end_date"
                value={filters.end_date}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      )}

      {/* No Client Selected State */}
      {!selectedClient && !loadingClients && (
        <div className="bg-white border rounded-xl p-12 text-center">
          <Phone className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-600 mb-2">
            No Client Selected
          </h3>
          <p className="text-gray-500">
            Please select a voice client from the dropdown above to view their
            call records.
          </p>
        </div>
      )}

      {/* Table - Only show when client is selected */}
      {selectedClient && (
        <>
          <div className="overflow-x-auto bg-white rounded-xl shadow border">
            <table className="min-w-full text-sm text-left">
              <thead className="bg-gray-100 text-gray-600 border-b text-xs uppercase">
                <tr>
                  <th className="px-4 py-3">Direction</th>
                  <th className="px-4 py-3">Date/Time</th>
                  <th className="px-4 py-3">From</th>
                  <th className="px-4 py-3">To</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Duration</th>
                  <th className="px-4 py-3">Billable</th>
                  <th className="px-4 py-3">Recording</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center text-gray-400 px-4 py-10"
                    >
                      <RefreshCcw className="w-6 h-6 animate-spin mx-auto mb-2" />
                      Loading CDRs...
                    </td>
                  </tr>
                ) : cdrs.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center text-gray-400 px-4 py-10"
                    >
                      <PhoneMissed className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      No call records found
                    </td>
                  </tr>
                ) : (
                  cdrs.map((cdr) => {
                    const direction =
                      cdr.direction ||
                      (cdr.dcontext?.includes("from-trunk") ||
                      cdr.dcontext?.includes("from-did")
                        ? "inbound"
                        : cdr.dcontext === "from-internal"
                          ? "outbound"
                          : "internal");

                    return (
                      <tr key={cdr.uniqueid} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <DirectionIcon direction={direction} />
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {formatDate(cdr.calldate)}
                        </td>
                        <td className="px-4 py-3 font-mono text-sm">
                          {cdr.src}
                        </td>
                        <td className="px-4 py-3 font-mono text-sm">
                          {cdr.dst}
                        </td>
                        <td className="px-4 py-3">
                          <DispositionBadge disposition={cdr.disposition} />
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {formatDuration(cdr.duration)}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {formatDuration(cdr.billsec)}
                        </td>
                        <td className="px-4 py-3">
                          {cdr.has_recording && cdr.recording_url ? (
                            <AudioPlayer
                              recordingUrl={cdr.recording_url}
                              uniqueId={cdr.uniqueid}
                            />
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center mt-6">
            <div className="text-sm text-gray-500">
              Showing {cdrs.length} of {total} records
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                className="flex items-center gap-1 px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={currentPage === 1 || loading}
              >
                <ArrowLeft size={16} /> Previous
              </button>
              <span className="text-sm text-gray-600 px-4">
                Page {currentPage} of {totalPages || 1}
              </span>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                className="flex items-center gap-1 px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={currentPage >= totalPages || loading}
              >
                Next <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}